<template>
    <div class="cl-navbar">
        <!-- ---------- Desktop: the charcoal bar with plain links (no v-tabs, so every link works at once) ---------- -->
        <header v-if="!$vuetify.breakpoint.mobile" class="cl-bar">
            <div class="cl-bar__inner">
                <router-link :to="{ name: 'Home' }" class="cl-bar__logo">
                    <img :src="require('@/assets/commercial-league-inline-logo.png')" alt="Commercial League">
                </router-link>

                <nav class="cl-bar__nav">
                    <!-- centred between the logo and the profile (auto margins: no clipping when it overflows) -->
                    <div class="cl-bar__links">
                        <template v-for="item in navItems">
                            <v-menu
                                v-if="item.children"
                                :key="item.key"
                                offset-y
                                bottom
                                transition="slide-y-transition"
                                content-class="cl-bar__menu"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <button
                                        type="button"
                                        class="cl-bar__link"
                                        :class="{ 'cl-bar__link--active': isGroupActive(item) }"
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        {{ item.name }}
                                        <v-icon small class="cl-bar__caret">mdi-chevron-down</v-icon>
                                    </button>
                                </template>
                                <v-list dense color="#232323" class="cl-bar__menu-list">
                                    <div v-if="!item.children.length" class="cl-bar__menu-empty">Σύντομα</div>
                                    <v-list-item
                                        v-for="child in item.children"
                                        :key="child.key"
                                        :to="child.to"
                                        class="cl-bar__menu-item"
                                        :class="{ 'cl-bar__menu-item--active': isActive(child) }"
                                    >
                                        <v-icon small class="cl-bar__menu-icon">{{ child.icon || item.icon }}</v-icon>
                                        <span class="cl-bar__menu-text">{{ child.name }}</span>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                            <router-link
                                v-else
                                :key="item.key"
                                :to="item.to"
                                class="cl-bar__link"
                                :class="{ 'cl-bar__link--active': isActive(item) }"
                            >
                                {{ item.name }}
                            </router-link>
                        </template>
                    </div>
                </nav>

                <!-- the signed-in player: the team logo with the player's photo (when there is one) as a badge -->
                <v-menu v-if="myTeam || myPlayer" offset-y left nudge-bottom="8" min-width="240" content-class="cl-bar__menu">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="cl-me" v-bind="attrs" v-on="on">
                            <span class="cl-me__avatars">
                                <span class="cl-me__team">
                                    <img v-if="myTeam && myTeam.logo" :src="`${assetsUrl}/${myTeam.logo}`" alt="">
                                    <v-icon v-else small color="teal">{{ myTeam ? 'mdi-account-group' : 'mdi-account' }}</v-icon>
                                </span>
                                <span v-if="myPlayer && myPlayer.image" class="cl-me__player">
                                    <img :src="`${assetsUrl}/${myPlayer.image}`" alt="">
                                </span>
                            </span>
                            <span class="cl-me__text">
                                <span class="cl-me__name">{{ myPlayer ? playerDisplayName : (myTeam ? myTeam.name : '') }}</span>
                                <span v-if="myPlayer && myTeam" class="cl-me__sub">{{ myTeam.name }}</span>
                            </span>
                            <v-icon small class="cl-bar__caret">mdi-chevron-down</v-icon>
                        </button>
                    </template>
                    <v-list dense color="#232323" class="cl-bar__menu-list">
                        <div v-if="myPlayer" class="cl-bar__greeting">Καλώς ήρθες, {{ greetingName }}!</div>
                        <v-list-item v-for="link in meLinks" :key="link.key" :to="link.to" class="cl-bar__menu-item">
                            <v-icon small class="cl-bar__menu-icon">{{ link.icon }}</v-icon>
                            <span class="cl-bar__menu-text">{{ link.name }}</span>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <router-link v-else :to="{ name: 'Profile' }" class="cl-me cl-me--empty">
                    <v-icon small class="cl-bar__menu-icon mr-0">mdi-star-plus-outline</v-icon>
                    <span class="cl-me__name">Διάλεξε ομάδα</span>
                </router-link>
            </div>
        </header>

        <!-- ---------- Mobile: a top bar, the menu on the left ---------- -->
        <header v-else class="cl-mbar">
            <button type="button" class="cl-mbar__menu" aria-label="Μενού" @click="drawer = true">
                <v-icon color="white">mdi-menu</v-icon>
                <span>Μενού</span>
            </button>
            <router-link :to="{ name: 'Home' }" class="cl-mbar__logo">
                <img :src="require('@/assets/commercial-league-inline-logo.png')" alt="Commercial League">
            </router-link>
        </header>

        <!-- ---------- Mobile: the menu ---------- -->
        <v-navigation-drawer
            v-if="$vuetify.breakpoint.mobile"
            v-model="drawer"
            app
            temporary
            disable-resize-watcher
            width="86vw"
            color="#1b1b1b"
            class="cl-drawer"
            style="z-index: 101;"
        >
            <div class="cl-drawer__header">
                <img :src="require('@/assets/main_logo.png')" alt="" class="cl-drawer__logo">
                <div class="cl-drawer__brand">
                    <div class="cl-drawer__brand-name">Commercial League</div>
                    <div v-if="myPlayer" class="cl-drawer__greeting">Καλώς ήρθες, {{ greetingName }}!</div>
                </div>
                <v-btn icon small dark @click="drawer = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>

            <!-- my team -->
            <router-link
                :to="myTeam ? { name: 'Team', params: { id: myTeam.id } } : { name: 'Profile' }"
                class="cl-drawer__team"
                @click.native="drawer = false"
            >
                <span class="cl-drawer__team-logo">
                    <img v-if="myTeam && myTeam.logo" :src="`${assetsUrl}/${myTeam.logo}`" alt="">
                    <v-icon v-else color="teal">{{ myTeam ? 'mdi-account-group' : 'mdi-star-plus-outline' }}</v-icon>
                </span>
                <span class="cl-drawer__team-text">
                    <span class="cl-drawer__team-label">{{ myTeam ? 'Η ομάδα μου' : 'Δεν έχεις διαλέξει ομάδα' }}</span>
                    <span class="cl-drawer__team-name">{{ myTeam ? myTeam.name : 'Διάλεξε ομάδα' }}</span>
                </span>
                <v-icon color="grey">mdi-chevron-right</v-icon>
            </router-link>

            <!-- my player and my settings, as in the account menu of the desktop -->
            <div class="cl-drawer__group">
                <router-link
                    v-for="link in drawerMeLinks"
                    :key="link.key"
                    :to="link.to"
                    class="cl-drawer__row"
                    :class="{ 'cl-drawer__row--active': isActive(link) }"
                    @click.native="drawer = false"
                >
                    <span v-if="link.image" class="cl-drawer__icon cl-drawer__icon--photo"><img :src="`${assetsUrl}/${link.image}`" alt=""></span>
                    <span v-else class="cl-drawer__icon cl-drawer__icon--soft"><v-icon small color="#03e5b7">{{ link.icon }}</v-icon></span>
                    <span class="cl-drawer__name">
                        {{ link.name }}
                        <span v-if="link.sub" class="cl-drawer__sub">{{ link.sub }}</span>
                    </span>
                    <v-icon small color="grey">mdi-chevron-right</v-icon>
                </router-link>
            </div>

            <div class="cl-drawer__section-title cl-drawer__section-title--static">Σελίδες</div>
            <div class="cl-drawer__group">
                <router-link
                    v-for="item in mainLinks"
                    :key="item.key"
                    :to="item.to"
                    class="cl-drawer__row"
                    :class="{ 'cl-drawer__row--active': isActive(item) }"
                    @click.native="drawer = false"
                >
                    <span class="cl-drawer__icon"><v-icon small color="white">{{ item.icon }}</v-icon></span>
                    <span class="cl-drawer__name">{{ item.name }}</span>
                    <v-icon small color="grey">mdi-chevron-right</v-icon>
                </router-link>
            </div>

            <!-- the leagues, the cups and the league pages: one section each, opened on tap -->
            <div v-for="section in drawerSections" :key="section.key" class="cl-drawer__section">
                <button type="button" class="cl-drawer__section-title" @click="toggle(section.key)">
                    <span>{{ section.name }}</span>
                    <span v-if="section.children.length" class="cl-drawer__count">{{ section.children.length }}</span>
                    <v-spacer />
                    <v-icon small color="grey">{{ openSections.includes(section.key) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </button>
                <div v-if="openSections.includes(section.key)" class="cl-drawer__group">
                    <div v-if="!section.children.length" class="cl-drawer__empty">Σύντομα</div>
                    <router-link
                        v-for="child in section.children"
                        :key="child.key"
                        :to="child.to"
                        class="cl-drawer__row"
                        :class="{ 'cl-drawer__row--active': isActive(child) }"
                        @click.native="drawer = false"
                    >
                        <span class="cl-drawer__icon"><v-icon small color="white">{{ child.icon || section.icon }}</v-icon></span>
                        <span class="cl-drawer__name">{{ child.name }}</span>
                        <v-icon small color="grey">mdi-chevron-right</v-icon>
                    </router-link>
                </div>
            </div>

            <div class="cl-drawer__socials">
                <button v-for="social in socials" :key="social.key" type="button" class="cl-drawer__social" @click="openSocial(social.key)">
                    <v-icon small color="white">{{ social.icon }}</v-icon>
                </button>
            </div>
        </v-navigation-drawer>
    </div>
</template>

<script>
    import { openSocial } from '@/utils/social'

    export default {
        name: 'NavBar',
        data() {
            return {
                drawer: false,
                openSections: [],
                assetsUrl: 'https://storage.googleapis.com',
                socials: [
                    { key: 'facebook', icon: 'mdi-facebook' },
                    { key: 'instagram', icon: 'mdi-instagram' },
                    { key: 'youtube', icon: 'mdi-youtube' },
                    { key: 'twitter', icon: 'mdi-twitter' },
                    { key: 'linkedin', icon: 'mdi-linkedin' }
                ]
            }
        },
        computed: {
            myPlayer() {
                return this.$store.getters.getPlayer
            },
            myTeam() {
                return this.$store.getters.getTeam
            },
            leagueGroups() {
                return this.$store.getters.getLeagueGroups || []
            },
            leagues() {
                return this.$store.getters.getLeagues || []
            },
            cupLeagues() {
                return this.$store.getters.getCupLeagues || []
            },
            // "Σκάγκος Ιωάννης": every word capitalised
            playerDisplayName() {
                if (!this.myPlayer || !this.myPlayer.name) return ''
                return this.myPlayer.name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
            },
            greetingName() {
                const parts = this.playerDisplayName.split(' ')
                // the name is kept as "surname name": greet with the last word
                return parts.length > 1 ? parts[parts.length - 1] : parts[0]
            },
            mainLinks() {
                return [
                    { key: 'Home', name: 'Home', icon: 'mdi-home-outline', to: { name: 'Home' } },
                    { key: 'News', name: 'News', icon: 'mdi-newspaper-variant-outline', to: { name: 'News' } },
                    { key: 'Teams', name: 'Teams', icon: 'mdi-account-group-outline', to: { name: 'Teams' } },
                    { key: 'Courts', name: 'Courts', icon: 'mdi-map-marker-outline', to: { name: 'Courts' } }
                ]
            },
            cupItem() {
                return {
                    key: 'cup',
                    name: 'Cup',
                    icon: 'mdi-trophy-outline',
                    children: this.cupLeagues.map(cup => ({ key: `cup-${cup.id}`, name: cup.title, to: { name: 'CupLeague', params: { id: cup.id } } }))
                }
            },
            // one entry per league group, with its leagues
            groupItems() {
                return this.leagueGroups.map(group => ({
                    key: `group-${group.id}`,
                    name: group.title,
                    icon: 'mdi-podium',
                    children: this.leagues
                        .filter(league => league.leagueGroup && league.leagueGroup.id === group.id)
                        .map(league => ({ key: `league-${league.id}`, name: league.title, to: { name: 'League', params: { id: league.id } } }))
                }))
            },
            leagueItem() {
                return {
                    key: 'the-league',
                    name: 'The League',
                    icon: 'mdi-information-outline',
                    children: [
                        { key: 'Register', name: 'Εγγραφή', icon: 'mdi-clipboard-account-outline', to: { name: 'Register' } },
                        { key: 'Regulations', name: 'Κανονισμός', icon: 'mdi-gavel', to: { name: 'Regulations' } },
                        { key: 'ContactUs', name: 'Επικοινωνία', icon: 'mdi-email-outline', to: { name: 'ContactUs' } }
                    ]
                }
            },
            navItems() {
                return [
                    ...this.mainLinks.slice(0, 3),
                    this.cupItem,
                    ...this.groupItems,
                    this.mainLinks[3],
                    this.leagueItem
                ]
            },
            drawerSections() {
                return [...this.groupItems, this.cupItem, this.leagueItem]
            },
            // on mobile the team has its own card, so only the player and the settings
            drawerMeLinks() {
                const links = []
                if (this.myPlayer) {
                    links.push({ key: 'player', name: 'Το προφίλ μου', sub: this.playerDisplayName, icon: 'mdi-account-outline', image: this.myPlayer.image, to: { name: 'Player', params: { id: this.myPlayer.id } } })
                }
                links.push({ key: 'settings', name: 'Ρυθμίσεις', sub: this.myPlayer ? null : 'Διάλεξε ομάδα και παίκτη', icon: 'mdi-cog-outline', to: { name: 'Profile' } })
                return links
            },
            meLinks() {
                const links = []
                if (this.myTeam) {
                    links.push({ key: 'team', name: 'Η ομάδα μου', icon: 'mdi-shield-star-outline', to: { name: 'Team', params: { id: this.myTeam.id } } })
                }
                if (this.myPlayer) {
                    links.push({ key: 'player', name: 'Το προφίλ μου', icon: 'mdi-account-outline', to: { name: 'Player', params: { id: this.myPlayer.id } } })
                }
                links.push({ key: 'settings', name: 'Ρυθμίσεις', icon: 'mdi-cog-outline', to: { name: 'Profile' } })
                return links
            }
        },
        watch: {
            $route() {
                this.drawer = false
            }
        },
        created() {
            this.$store.dispatch('ensureLeagueGroups')
            this.$store.dispatch('ensureLeagues')
            this.$store.dispatch('ensureCupLeagues')
        },
        methods: {
            openSocial,
            isActive(item) {
                if (!item.to || item.to.name !== this.$route.name) return false
                return !item.to.params || String(item.to.params.id) === String(this.$route.params.id)
            },
            isGroupActive(item) {
                return item.children.some(child => this.isActive(child))
            },
            toggle(key) {
                this.openSections = this.openSections.includes(key)
                    ? this.openSections.filter(item => item !== key)
                    : [...this.openSections, key]
            }
        }
    }
</script>

<style>
/* the dropdowns are rendered outside the bar */
.cl-bar__menu {
    border-radius: 10px !important;
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.35) !important;
}

.cl-bar__menu-list {
    padding: 6px !important;
    border-top: 3px solid #03e5b7;
    background: #232323 !important;
}

.cl-bar__menu-item {
    min-height: 40px !important;
    border-radius: 6px;
}

.cl-bar__menu-item::before {
    border-radius: 6px;
}

.cl-bar__menu-item:hover,
.cl-bar__menu-item--active {
    background: rgba(3, 229, 183, 0.1);
}

.cl-bar__menu-icon {
    margin-right: 12px;
    color: #03e5b7 !important;
}

.cl-bar__menu-text {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    color: #fff;
}

.cl-bar__menu-empty,
.cl-bar__greeting {
    padding: 8px 12px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
}

.cl-bar__greeting {
    margin-bottom: 4px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    color: #03e5b7;
    font-weight: 700;
}
</style>

<style scoped>
/* ---------- desktop bar ---------- */
.cl-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    height: 64px;
    background: #232323;
    box-shadow: 0 2px 14px rgba(0, 0, 0, 0.25);
}

.cl-bar::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -3px;
    height: 3px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.cl-bar__inner {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 100%;
    padding: 0 16px;
}

.cl-bar__logo img {
    display: block;
    width: 200px;
}

.cl-bar__nav {
    display: flex;
    align-items: stretch;
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    overflow-x: auto;
    scrollbar-width: none;
}

.cl-bar__links {
    display: flex;
    align-items: stretch;
    height: 100%;
    margin: 0 auto;
}

.cl-bar__nav::-webkit-scrollbar {
    display: none;
}

.cl-bar__link {
    position: relative;
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    padding: 0 11px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 20px;
    letter-spacing: 0.03em;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.82) !important;
    text-decoration: none;
    transition: color 0.15s;
}

.cl-bar__link:hover,
.cl-bar__link[aria-expanded='true'] {
    color: #03e5b7 !important;
}

/* the page you are on: the gradient underline of the league */
.cl-bar__link--active {
    color: #fff !important;
}

.cl-bar__link--active::after {
    content: '';
    position: absolute;
    left: 11px;
    right: 11px;
    bottom: 0;
    height: 4px;
    border-radius: 4px 4px 0 0;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

/* a smaller bar on laptops, so every league fits */
@media (max-width: 1500px) {
    .cl-bar__logo img {
        width: 150px;
    }

    .cl-bar__link {
        padding: 0 8px;
        font-size: 18px;
    }

    .cl-bar__link--active::after {
        left: 8px;
        right: 8px;
    }

    .cl-me__text {
        max-width: 120px;
    }
}

.cl-bar__caret {
    margin-left: 2px;
    color: rgba(255, 255, 255, 0.5) !important;
}

/* ---------- the account chip ---------- */
.cl-me {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
    margin-left: auto;
    max-width: 240px;
    padding: 4px 10px 4px 4px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #fff !important;
    text-align: left;
    text-decoration: none;
    transition: background 0.15s, border-color 0.15s;
}

.cl-me:hover,
.cl-me[aria-expanded='true'] {
    background: rgba(3, 229, 183, 0.1);
    border-color: rgba(3, 229, 183, 0.5);
}

.cl-me--empty {
    padding: 8px 14px;
}

.cl-me__avatars {
    position: relative;
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
}

.cl-me__team,
.cl-me__player {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.cl-me__team {
    inset: 0;
    border-radius: 50%;
    background: #fff;
}

.cl-me__team img {
    max-width: 86%;
    max-height: 86%;
    object-fit: contain;
}

/* the player's photo: a small badge on the corner of the team logo */
.cl-me__player {
    right: -4px;
    bottom: -4px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3a3a3a;
    box-shadow: 0 0 0 2px #232323, 0 0 0 3px #03e5b7;
}

.cl-me__player img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

.cl-me__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.15;
}

.cl-me__name {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.05rem;
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cl-me__sub {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ---------- mobile bar ---------- */
.cl-mbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    gap: 12px;
    height: 56px;
    padding: 0 12px;
    background: #232323;
}

.cl-mbar::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -3px;
    height: 3px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.cl-mbar__menu {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
    color: #fff;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.2rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.cl-mbar__menu:active {
    color: #03e5b7;
}

.cl-mbar__logo {
    display: flex;
    align-items: center;
    margin-left: auto;
}

.cl-mbar__logo img {
    display: block;
    width: 170px;
}

/* ---------- mobile menu ---------- */
.cl-drawer {
    color: #fff;
    text-align: left;
}

.cl-drawer__header {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 14px 16px;
    background:
        radial-gradient(circle at 90% 0%, rgba(3, 229, 183, 0.3) 0%, rgba(3, 229, 183, 0) 55%),
        #232323;
}

.cl-drawer__header::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.cl-drawer__logo {
    width: 44px;
    height: 44px;
    padding: 4px;
    border-radius: 10px;
    background: #fff;
    object-fit: contain;
}

.cl-drawer__brand {
    flex: 1 1 auto;
    min-width: 0;
}

.cl-drawer__brand-name {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.4rem;
    line-height: 1.1;
    text-transform: uppercase;
}

.cl-drawer__greeting {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    color: #03e5b7;
}

.cl-drawer__team {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 14px 12px 6px;
    padding: 12px;
    border-radius: 12px;
    background: linear-gradient(315deg, rgba(3, 229, 183, 0.22) 0%, rgba(3, 122, 222, 0.22) 74%);
    border: 1px solid rgba(3, 229, 183, 0.35);
    color: #fff !important;
    text-decoration: none;
}

.cl-drawer__team-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #fff;
    overflow: hidden;
}

.cl-drawer__team-logo img {
    max-width: 86%;
    max-height: 86%;
    object-fit: contain;
}

.cl-drawer__team-text {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
}

.cl-drawer__team-label {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.65);
}

.cl-drawer__team-name {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.3rem;
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cl-drawer__group {
    margin: 6px 12px;
    border-radius: 12px;
    background: #262626;
    overflow: hidden;
}

.cl-drawer__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    color: #fff !important;
    text-decoration: none;
}

.cl-drawer__row + .cl-drawer__row {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.cl-drawer__row--active {
    background: rgba(3, 229, 183, 0.12);
}

.cl-drawer__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}

.cl-drawer__name {
    flex: 1 1 auto;
    min-width: 0;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.95rem;
    font-weight: 600;
}

.cl-drawer__sub {
    display: block;
    font-size: 0.75rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.55);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cl-drawer__icon--soft {
    background: rgba(3, 229, 183, 0.12);
}

.cl-drawer__icon--photo {
    background: #3a3a3a;
    overflow: hidden;
}

.cl-drawer__icon--photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

.cl-drawer__section-title--static {
    cursor: default;
}

.cl-drawer__section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 16px 16px 6px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.2rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #03e5b7;
    text-align: left;
}

.cl-drawer__count {
    padding: 0 7px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.7rem;
    font-weight: 700;
}

.cl-drawer__empty {
    padding: 12px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.5);
}

.cl-drawer__socials {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 22px 12px 28px;
}

.cl-drawer__social {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
}

.cl-drawer__social:active {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}
</style>
